import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { getLocations } from 'actions/location'
import Spinner from 'components/Spinner'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import LocationModePicker from 'scenes/RecoveryAI/components/LocationModePicker'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { useActiveLocationsController } from 'scenes/RecoveryAI/components/useActiveLocationsController'
import { useStyletron } from 'styletron-react'
import PilotPerformanceBanner from './ActiveLocations/PilotPerformanceBanner'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

// AI-901 — dev-only gate for the pilot-vs-staff performance callout. Kept in
// development until the option-A backend supplies real per-merchant figures;
// while on but un-backed, the banner renders nothing (no invented figures).
const PERFORMANCE_BANNER_FLAG =
  'recovery-ai-active-locations-performance-banner'

const t = buildTranslate('recovery_ai.settings.active_locations')

/**
 * Active Locations Settings pane (AI-31). A radio pair — "All N locations
 * (recommended)" vs "A subset of locations" — where the subset mode reveals a
 * controlled multiselect + a removable address-row list. All data, hydration,
 * dirty tracking and the two-write save live in useActiveLocationsController,
 * which the Activation modal shares; this file is the pane's chrome around it:
 * the load / fetch-error / locked branches and the banners.
 *
 * AI-900: no Save button here — this pane opts into the controller's
 * debounced autosave (`autosave: true`), so a toggle/mode change persists on
 * its own ~AUTOSAVE_DEBOUNCE_MS after the last edit. The Activation modal
 * keeps the original explicit-Save flow (it doesn't pass the option).
 */
const ActiveLocationsPane = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const showPerformanceBanner = useFlag(PERFORMANCE_BANNER_FLAG, false)
  const locations = useActiveLocationsController({
    skipWhenNotOnboarded: true,
    // AI-900: this pane debounce-autosaves toggles/mode changes instead of
    // requiring an explicit Save click (see useActiveLocationsController).
    // The Activation modal (ActiveLocationsConfigRow) omits this, keeping its
    // explicit-Save flow unchanged.
    autosave: true,
  })

  // AI-1173 follow-up: the Redux-ORM Location list isn't loaded anywhere near
  // RecoveryAI — useActiveLocationsController reads it to append a blank-name
  // row's street address. Intentionally unfiltered (no coming_soon param) so
  // its scope matches GET recovery/location_configs, which has no
  // coming_soon predicate — a coming-soon location must still be
  // address-enrichable. `dispatch` is referentially stable, so this fires
  // once per mount (mirrors PurchasesReport/RevenueReport's location fetch).
  useEffect(() => {
    dispatch(getLocations())
  }, [dispatch])

  // R3: warn before leaving the route (in-app nav + refresh/close) while dirty.
  // Keyed on locationsDirty — an ACTUAL user edit — not the combined isDirty: a
  // legacy full-coverage merchant is defaultDirty on load (persisted flag false
  // vs derived-true coverage) with no edit, and gating the prompt on isDirty
  // would false-warn on merely viewing then leaving. Any real edit flips
  // locationsDirty, so no legitimate warning is lost; the flag reconcile is
  // still offered via autosave. Stays in the PANE: a <Prompt> guards route
  // transitions, which is the wrong tool inside a modal.
  //
  // AI-900 (review follow-up): ONLY the in-app <Prompt> is suppressed while
  // pendingAutosave — an in-app nav is now safe because unmounting FLUSHES the
  // armed/queued commit (see useActiveLocationsController's cleanup). A tab
  // close/refresh can't be flushed reliably, so the beforeunload channel stays
  // armed on the raw `locationsDirty` regardless of pendingAutosave — a
  // merchant who toggles and immediately closes the tab still gets warned.
  const unsavedChangesPrompt = useUnsavedChangesPrompt(
    locations.locationsDirty,
    undefined,
    { skipInAppPrompt: locations.pendingAutosave }
  )

  // Block on either load-bearing fetch (config gates activation; the location
  // list is the source of truth for mode + selection). Metrics is NOT
  // load-bearing — an absent N degrades to an em-dash, not a spinner.
  if (locations.isLoading) {
    return (
      <div role="status" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // A failed FETCH (config OR the location list) surfaces an error/retry
  // message, NOT the "locked" copy (which would imply the merchant simply
  // hasn't activated) and NOT a silent empty picker. Checked first. Now that
  // #619 is merged, a 404/500 from the GET is a real error, not a gated-empty.
  if (locations.isFetchError) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="alert">
        <Text.BodyText3 color="cayenne50">{t('fetch_error')}</Text.BodyText3>
      </div>
    )
  }

  if (locations.isLocked) {
    return (
      <div
        className={`p-l ${css({ textAlign: 'center' })}`}
        role="status"
        aria-label={t('locked')}
      >
        <Text.BodyText3 color="grey70">{t('locked')}</Text.BodyText3>
      </div>
    )
  }

  // Subset mode reveals the selected-locations section, so the empty-selection
  // validation below only applies there (in "all" mode there is nothing to
  // deselect down to zero).
  const showSubset = locations.mode === 'subset'
  // AI-900: mirrors useActiveLocationsController's own autosave-invalid guard
  // (a debounced commit never fires here) — shown inline since there's no
  // longer a Save button to disable/tooltip.
  const isEmptySubset = showSubset && locations.selectedIds.length === 0

  return (
    // contentGap="l" (32px): matches the mb-l below the performance banner so
    // subtitle→banner and banner→radio-group land at the same distance,
    // instead of the chrome default xl (48px) sitting uneven against it.
    <SettingsPaneChrome
      title={t('title')}
      subtitle={t('subtitle')}
      contentGap="l"
    >
      {/* R3: guard whole-route navigation (in-app + refresh/close) while dirty. */}
      {unsavedChangesPrompt}
      {/* AI-901: pilot-vs-staff performance callout at the TOP, in subset mode
          only (the "N pilot locations" framing has no meaning under "all"). Dev-
          gated; populated from real metrics only — the banner renders nothing
          until the backend supplies a complete set, and self-hides when real
          data shows RAI is not winning. */}
      {showPerformanceBanner && showSubset && (
        <div className="mb-l">
          <PilotPerformanceBanner performance={locations.pilotPerformance} />
        </div>
      )}
      {locations.isSaveError && (
        // baseui's Notification (Toast) hardcodes role="alert" on its Body for
        // assistive-tech announcement on insertion. The error keeps that default
        // assertive role, which is correct here.
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {locations.isConfigOnlyError
              ? t('save_error_config_only')
              : t('save_error')}
          </Notification>
          {/* AI-900 (review follow-up): there is no Save button on this pane to
              retry with, so the recovery affordance rides beside the failure —
              mirrors EscalationRouting's retry_save pattern. Bound to commit()
              directly (not a separate wrapper): any edit already clears
              isSaveError via clearSaveState, so Retry is only ever visible for
              the EXACT staged state that just failed — commit() re-fires the
              same payload, never a no-op. Disabled on pendingAutosave (not
              isSaving) so it stays disabled for the WHOLE real write: an edit
              landing mid-flight resets the mutation hook, which drops isSaving
              back to false while the PUT is still on the wire. */}
          <div className="mt-s">
            <Button
              kind="secondary"
              onClick={locations.commit}
              disabled={locations.pendingAutosave}
            >
              {t('retry')}
            </Button>
          </div>
        </div>
      )}
      {/* No positive "saved" banner here (Darshan QA): with autosave the
          "Saving…" indicator is the confirmation, and a banner popping in after
          every toggle shifts the picker below it. The wizard step keeps its
          banner — it still has a manual Save. */}
      <LocationModePicker
        mode={locations.mode}
        onModeChange={locations.onModeChange}
        locations={locations.locations}
        selectedIds={locations.selectedIds}
        onToggle={locations.onToggle}
        onRemove={locations.onRemove}
        selectedLocations={locations.selectedLocations}
        canManage={locations.canManage}
        instanceId="settings"
        labels={locations.labels}
      />

      {/* AI-900: no Save button — edits debounce-autosave (see
          useActiveLocationsController). This is the replacement for the old
          disabled-Save affordance: an empty subset selection never
          autosaves, so say so inline (there's no button left to tooltip);
          otherwise show a lightweight in-progress indicator. Keyed on
          pendingAutosave (armed OR in flight), not isSaving alone — review
          follow-up: gating on isSaving left a silent gap for the whole
          ~300ms debounce window before the PUT actually fired. Both states
          are announced via role="status" + aria-live="polite" (R5) without
          interrupting. */}
      {locations.canManage && (isEmptySubset || locations.pendingAutosave) && (
        <div className="mt-l" role="status" aria-live="polite">
          {isEmptySubset ? (
            <Text.BodyText4 color="grey70">
              {t('save_disabled_hint_empty')}
            </Text.BodyText4>
          ) : (
            <div className="d-flex align-items-center">
              <Spinner isLoading size="1x" className="mr-xs" />
              <Text.BodyText4 color="grey70">{t('saving')}</Text.BodyText4>
            </div>
          )}
        </div>
      )}
    </SettingsPaneChrome>
  )
}

export default ActiveLocationsPane
